(function () {
  "use strict";

  const MAX_PRODUCTS = 6;
  const MAX_TERMS = 3;
  const MAX_VISIBLE_FACETS = 4;
  const MAX_VISIBLE_VALUES = 8;
  const SESSION_KEY = "amp_ai_parts_finder_session_v2";

  function readVehicle() {
    try {
      const vehicle = JSON.parse(window.localStorage.getItem("vehicle") || "null");
      return vehicle && typeof vehicle === "object" ? vehicle : null;
    } catch (_error) {
      return null;
    }
  }

  function vehicleTargetId(vehicle) {
    const targetId = String(vehicle?.linkageTargetId || vehicle?.engineId || "").trim();
    return /^\d{1,12}$/.test(targetId) ? targetId : "";
  }

  function clean(value, maxLength) {
    return String(value || "")
      .replace(/[\u0000-\u001f\u007f]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, maxLength);
  }

  function compactVehicleContext(vehicle) {
    const targetId = vehicleTargetId(vehicle);
    if (!targetId) return null;
    const engineCapacityCc = clean(vehicle?.engineCapacityCC || vehicle?.capacityCC, 8);
    const rawPowerHp = clean(vehicle?.engineOutputInHP, 8);
    const powerHp = rawPowerHp && rawPowerHp !== engineCapacityCc && Number(rawPowerHp) <= 1000
      ? rawPowerHp
      : "";
    return {
      targetId,
      make: clean(vehicle?.mark || vehicle?.manufacturer, 50),
      model: clean(vehicle?.vehicleModelSeriesName || vehicle?.model, 90),
      description: clean(vehicle?.vehicleDescription || vehicle?.engine, 120),
      body: clean(vehicle?.bodyTypeOfVehicle || vehicle?.bodyStyle, 60),
      engineCapacityCc,
      powerHp,
      fuel: clean(vehicle?.fuel || vehicle?.fuelType, 50),
      year: clean(vehicle?.mfrYear || vehicle?.year, 4),
    };
  }

  function vehicleLabel(vehicle) {
    const registration = clean(vehicle?.registration, 10).toUpperCase();
    const make = clean(vehicle?.mark || vehicle?.manufacturer, 50);
    const model = clean(vehicle?.vehicleModelSeriesName || vehicle?.model, 90);
    const description = clean(vehicle?.vehicleDescription || vehicle?.engine, 120);
    const details = [make, description || model].filter((value, index, values) => {
      if (!value) return false;
      return values.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index;
    });
    if (registration && details.length) return `${registration}: ${details.join(" ")}`;
    return registration || details.join(" ") || "No vehicle selected";
  }

  function sessionId() {
    try {
      let value = window.sessionStorage.getItem(SESSION_KEY);
      if (/^[a-zA-Z0-9_-]{12,64}$/.test(value || "")) return value;
      value = typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `session_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
      window.sessionStorage.setItem(SESSION_KEY, value);
      return value;
    } catch (_error) {
      return `session_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
    }
  }

  function safeStorePath(value, fallback = "/search") {
    try {
      const url = new URL(String(value || fallback), window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith("/")) return fallback;
      return `${url.pathname}${url.search}`;
    } catch (_error) {
      return fallback;
    }
  }

  function categoryUrl(path, vehicle) {
    const url = new URL(safeStorePath(path), window.location.origin);
    const targetId = vehicleTargetId(vehicle);
    if (targetId && url.pathname.startsWith("/collections/")) {
      url.searchParams.delete("filter.v.m.car.ids");
      url.searchParams.append("filter.v.m.car.ids", targetId);
    }
    return `${url.pathname}${url.search}`;
  }

  function vehicleSearchUrl(term, vehicle, view = "", refinements = new Map()) {
    const url = new URL("/search", window.location.origin);
    url.searchParams.set("q", clean(term, 80));
    url.searchParams.set("type", "product");
    url.searchParams.set("options[prefix]", "last");
    if (view) url.searchParams.set("view", view);
    const targetId = vehicleTargetId(vehicle);
    if (targetId) {
      url.searchParams.append("filter.v.m.car.ids", targetId);
    }
    refinements.forEach((value, key) => {
      if (/^filter\.p\.(?:vendor|m\.custom\.(?:product_type|refinement_\d{2}))$/.test(key)) {
        url.searchParams.append(key, value);
      }
    });
    return url;
  }

  function createResultLink(item, vehicle, sendEvent) {
    const link = document.createElement("a");
    link.className = "amp-ai-finder__result";
    link.href = categoryUrl(item.path, vehicle);
    link.addEventListener("click", () => sendEvent("browse_all", {
      category: clean(item.id, 50),
      value: safeStorePath(item.path),
    }));

    const title = document.createElement("span");
    title.className = "amp-ai-finder__result-title";
    title.textContent = clean(item.title, 100) || "Relevant parts";

    const reason = document.createElement("span");
    reason.className = "amp-ai-finder__result-reason";
    reason.textContent = clean(item.why, 220);

    const arrow = document.createElement("span");
    arrow.className = "amp-ai-finder__result-arrow";
    arrow.setAttribute("aria-hidden", "true");
    arrow.textContent = ">";
    link.append(title, reason, arrow);
    return link;
  }

  function createCheck(text) {
    const item = document.createElement("li");
    item.textContent = text;
    return item;
  }

  function verifiedVehicleProduct(product, targetId) {
    if (!targetId || !Array.isArray(product?.fitmentVariants)) return null;
    const matchingVariants = product.fitmentVariants.filter((variant) =>
      /^\d+$/.test(String(variant?.id || "")) && Array.isArray(variant.fitmentIds) &&
      variant.fitmentIds.some((id) => String(id).trim() === targetId)
    );
    const variant = matchingVariants.find((item) => item.available === true && item.title === "1") ||
      matchingVariants.find((item) => item.available === true) || matchingVariants[0];
    if (!variant) return null;
    return {
      ...product,
      variantId: String(variant.id),
      price: variant.price,
      priceCents: variant.priceCents,
      available: variant.available === true,
      canAdd: variant.available === true && (variant.title === "1" || product.fitmentVariants.length === 1),
      matchType: "exact",
      vehicleTargetId: targetId,
    };
  }

  async function addProductToCart(product, button, sendEvent) {
    const variantId = String(product?.variantId || "");
    const targetId = vehicleTargetId(readVehicle());
    const verified = verifiedVehicleProduct(product, targetId);
    if (!targetId || targetId !== product.vehicleTargetId || !verified?.canAdd || verified.variantId !== variantId) return;
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = "Adding...";
    try {
      const miniCart = document.querySelector("mini-cart");
      const sections = miniCart?.getSectionsToRender?.().map((section) => section.section) || [];
      const response = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || data?.status) throw new Error(data?.description || "Could not add this item.");
      miniCart?.renderContents?.(data);
      if (window.themeSettings?.redirectToCart) {
        window.location.assign(window.routes?.cart_url || "/cart");
        return;
      }
      miniCart?.open?.(button);
      document.dispatchEvent(new CustomEvent("cart:item-add", {
        detail: { items: data?.items || [], context: "AI Parts Finder" },
      }));
      button.textContent = "Added";
      button.dataset.added = "true";
      sendEvent("add_to_cart", { category: product.matchType, value: variantId });
    } catch (error) {
      button.textContent = "Try again";
      button.removeAttribute("data-added");
      sendEvent("add_to_cart_error", { category: product.matchType, value: variantId });
      window.setTimeout(() => { button.textContent = originalText; }, 2500);
      console.error("AI Parts Finder cart error", error);
    } finally {
      button.disabled = false;
    }
  }

  function createProductCard(product, sendEvent) {
    const card = document.createElement("article");
    card.className = "amp-ai-finder__product";
    card.dataset.matchType = product.matchType;

    const link = document.createElement("a");
    link.className = "amp-ai-finder__product-link";
    const url = new URL(safeStorePath(product.url, "/search"), window.location.origin);
    if (/^\d+$/.test(String(product.variantId || ""))) url.searchParams.set("variant", product.variantId);
    link.href = `${url.pathname}${url.search}`;
    link.addEventListener("click", (event) => {
      if (vehicleTargetId(readVehicle()) !== product.vehicleTargetId) {
        event.preventDefault();
        window.dispatchEvent(new Event("amp:vehicle-changed"));
        return;
      }
      sendEvent("product_click", { category: product.matchType, value: String(product.id || "") });
    });

    const media = document.createElement("span");
    media.className = "amp-ai-finder__product-media";
    if (product.image) {
      const image = document.createElement("img");
      image.src = product.image;
      image.alt = "";
      image.loading = "lazy";
      image.width = 220;
      image.height = 220;
      media.append(image);
    }

    const copy = document.createElement("span");
    copy.className = "amp-ai-finder__product-copy";
    const match = document.createElement("span");
    match.className = "amp-ai-finder__product-match";
    match.dataset.matchType = product.matchType;
    match.textContent = "Confirmed for selected vehicle";

    const vendor = document.createElement("span");
    vendor.className = "amp-ai-finder__product-vendor";
    vendor.textContent = clean(product.vendor, 80) || "AutoMotoPart";
    const title = document.createElement("span");
    title.className = "amp-ai-finder__product-title";
    title.textContent = clean(product.title, 180);
    const footer = document.createElement("span");
    footer.className = "amp-ai-finder__product-footer";
    const price = document.createElement("span");
    price.className = "amp-ai-finder__product-price";
    price.textContent = clean(product.price, 40) || "View product";
    const availability = document.createElement("span");
    availability.className = "amp-ai-finder__product-stock";
    availability.dataset.available = product.available ? "true" : "false";
    availability.textContent = product.available ? "In stock" : "Out of stock";
    footer.append(price, availability);
    copy.append(match, vendor, title, footer);
    link.append(media, copy);
    card.append(link);

    if (product.available && product.canAdd === true && /^\d+$/.test(String(product.variantId || ""))) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "amp-ai-finder__product-add";
      button.textContent = "Add to basket";
      button.addEventListener("click", () => addProductToCart(product, button, sendEvent));
      card.append(button);
    }
    return card;
  }

  function normaliseTerms(searchTerms) {
    return Array.isArray(searchTerms)
      ? [...new Set(searchTerms.map((term) => clean(term, 80)).filter(Boolean))].slice(0, MAX_TERMS)
      : [];
  }

  function normaliseFacets(facets) {
    if (!Array.isArray(facets)) return [];
    return facets
      .filter((facet) => facet && typeof facet.paramName === "string" && Array.isArray(facet.values))
      .map((facet) => ({
        label: clean(facet.label, 80),
        paramName: facet.paramName,
        values: facet.values
          .filter((value) => value && clean(value.value, 120) && Number(value.count) > 0)
          .map((value) => ({
            label: clean(value.label, 100),
            value: clean(value.value, 120),
            count: Math.max(0, Number(value.count) || 0),
            active: Boolean(value.active),
          })),
      }))
      .filter((facet) => facet.label && facet.values.length > 0)
      .sort((a, b) => {
        const rank = (facet) => facet.paramName.includes("product_type")
          ? 0
          : facet.paramName === "filter.p.vendor" ? 1 : 2;
        return rank(a) - rank(b) || a.paramName.localeCompare(b.paramName);
      });
  }

  async function fetchVehicleProducts(searchTerms, vehicle, refinements, signal) {
    const products = [];
    const seen = new Set();
    const terms = normaliseTerms(searchTerms);
    let facets = [];
    let count = 0;
    const targetId = vehicleTargetId(vehicle);
    if (!targetId) return { products, facets, count, primaryTerm: terms[0] || "car parts" };
    for (const term of terms) {
      if (products.length >= MAX_PRODUCTS) break;
      try {
        const response = await fetch(vehicleSearchUrl(term, vehicle, "ai-parts-finder", refinements), {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          signal,
        });
        if (!response.ok) continue;
        const data = await response.json().catch(() => null);
        if (!data) continue;
        if (facets.length === 0) facets = normaliseFacets(data.facets);
        count += Math.max(0, Number(data.count) || 0);
        const matches = Array.isArray(data.products) ? data.products : [];
        for (const candidate of matches) {
          const key = String(candidate?.id || candidate?.url || "");
          if (!key || seen.has(key) || !candidate?.title || !candidate?.url) continue;
          const product = verifiedVehicleProduct(candidate, targetId);
          if (!product) continue;
          seen.add(key);
          products.push(product);
          if (products.length >= MAX_PRODUCTS) break;
        }
      } catch (error) {
        if (error?.name === "AbortError") throw error;
      }
    }
    return { products, facets, count, primaryTerm: terms[0] || "car parts" };
  }

  function initialise(root) {
    if (root.dataset.initialised === "true") return;
    root.dataset.initialised = "true";

    const form = root.querySelector("[data-ai-parts-finder-form]");
    const symptomPanel = root.querySelector("[data-ai-parts-finder-symptom-panel]");
    const textarea = root.querySelector("[data-ai-parts-finder-input]");
    const submit = root.querySelector("[data-ai-parts-finder-submit]");
    const status = root.querySelector("[data-ai-parts-finder-status]");
    const advicePanel = root.querySelector("[data-ai-parts-finder-advice-panel]");
    const followUp = root.querySelector("[data-ai-parts-finder-follow-up]");
    const questionNode = root.querySelector("[data-ai-parts-finder-question]");
    const optionsNode = root.querySelector("[data-ai-parts-finder-options]");
    const answerInput = root.querySelector("[data-ai-parts-finder-answer]");
    const continueButton = root.querySelector("[data-ai-parts-finder-continue]");
    const followUpStatus = root.querySelector("[data-ai-parts-finder-follow-up-status]");
    const results = root.querySelector("[data-ai-parts-finder-results]");
    const resultsHeading = root.querySelector("[data-ai-parts-finder-results-heading]");
    const summary = root.querySelector("[data-ai-parts-finder-summary]");
    const list = root.querySelector("[data-ai-parts-finder-list]");
    const safety = root.querySelector("[data-ai-parts-finder-safety]");
    const modeNote = root.querySelector("[data-ai-parts-finder-mode]");
    const vehicleName = root.querySelector("[data-ai-parts-finder-vehicle]");
    const vehicleLink = root.querySelector("[data-ai-parts-finder-vehicle-link]");
    const vehicleRequired = root.querySelector("[data-ai-parts-finder-vehicle-required]");
    const registrationForm = root.querySelector("[data-ai-parts-finder-registration-form]");
    const registrationInput = root.querySelector("[data-ai-parts-finder-registration]");
    const registrationSubmit = root.querySelector("[data-ai-parts-finder-registration-submit]");
    const registrationStatus = root.querySelector("[data-ai-parts-finder-registration-status]");
    const checksPanel = root.querySelector("[data-ai-parts-finder-checks-panel]");
    const checksList = root.querySelector("[data-ai-parts-finder-checks]");
    const productsPanel = root.querySelector("[data-ai-parts-finder-products-panel]");
    const productsStatus = root.querySelector("[data-ai-parts-finder-products-status]");
    const productGrid = root.querySelector("[data-ai-parts-finder-products]");
    const browseResults = root.querySelector("[data-ai-parts-finder-browse]");
    const refinementsPanel = root.querySelector("[data-ai-parts-finder-refinements]");
    const facetsNode = root.querySelector("[data-ai-parts-finder-facets]");
    const clearRefinements = root.querySelector("[data-ai-parts-finder-clear-refinements]");
    const feedback = root.querySelector("[data-ai-parts-finder-feedback]");
    const feedbackStatus = root.querySelector("[data-ai-parts-finder-feedback-status]");
    const endpoint = root.dataset.endpoint;
    const eventEndpoint = endpoint.replace(/\/recommendations\/?$/, "/events");
    const visitorSessionId = sessionId();
    let vehicle = readVehicle();
    let conversation = null;
    let currentQuestion = null;
    let initialSymptom = "";
    let lastAdvice = null;
    let activeRefinements = new Map();
    let catalogueController = null;
    let adviceController = null;
    let vehicleRevision = 0;

    function sendEvent(event, details = {}) {
      const payload = {
        event,
        sessionId: visitorSessionId,
        mode: clean(details.mode || lastAdvice?.mode, 30),
        category: clean(details.category, 50),
        value: clean(details.value, 80),
        resultCount: Math.max(0, Number(details.resultCount) || 0),
      };
      fetch(eventEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }

    function syncVehicle() {
      const selectedVehicle = readVehicle();
      if (vehicleTargetId(selectedVehicle) !== vehicleTargetId(vehicle)) resetAdviser(false);
      vehicle = selectedVehicle;
      const validVehicle = Boolean(compactVehicleContext(vehicle));
      vehicleName.textContent = vehicleLabel(vehicle);
      vehicleRequired.hidden = validVehicle;
      advicePanel.hidden = !validVehicle || !results.hidden;
      vehicleLink.hidden = !validVehicle;
      if (validVehicle) registrationStatus.textContent = "";
      return validVehicle;
    }

    function resetAdviser(track = true) {
      vehicleRevision += 1;
      adviceController?.abort();
      catalogueController?.abort();
      adviceController = null;
      catalogueController = null;
      conversation = null;
      currentQuestion = null;
      initialSymptom = "";
      lastAdvice = null;
      activeRefinements = new Map();
      textarea.value = "";
      answerInput.value = "";
      status.textContent = "";
      followUpStatus.textContent = "";
      feedbackStatus.textContent = "";
      optionsNode.replaceChildren();
      productGrid.replaceChildren();
      productGrid.removeAttribute("aria-busy");
      productsStatus.textContent = "";
      browseResults.hidden = true;
      list.replaceChildren();
      facetsNode.replaceChildren();
      results.hidden = true;
      followUp.hidden = true;
      symptomPanel.hidden = false;
      advicePanel.hidden = !compactVehicleContext(readVehicle());
      productsPanel.hidden = true;
      refinementsPanel.hidden = true;
      feedback.hidden = true;
      feedback.querySelectorAll("button").forEach((button) => { button.disabled = false; });
      setBusy(submit, false, "", "Find likely parts");
      setBusy(continueButton, false, "", "Continue");
      if (track) sendEvent("start_again");
    }

    function setBusy(button, busy, busyText, normalText) {
      button.disabled = busy;
      button.textContent = busy ? busyText : normalText;
    }

    function renderQuestion(data) {
      conversation = data.conversation;
      currentQuestion = data.question;
      questionNode.textContent = clean(data.question?.text, 180);
      answerInput.value = "";
      followUpStatus.textContent = "";
      optionsNode.replaceChildren();
      (Array.isArray(data.question?.options) ? data.question.options : []).forEach((option) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "amp-ai-finder__follow-up-option";
        button.textContent = clean(option, 100);
        button.addEventListener("click", () => {
          optionsNode.querySelectorAll("button").forEach((node) => {
            node.classList.toggle("is-selected", node === button);
            node.setAttribute("aria-pressed", node === button ? "true" : "false");
          });
          answerInput.value = button.textContent;
        });
        button.setAttribute("aria-pressed", "false");
        optionsNode.append(button);
      });
      symptomPanel.hidden = true;
      results.hidden = true;
      followUp.hidden = false;
      followUp.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    function createFacetGroup(facet, refreshProducts) {
      const fieldset = document.createElement("fieldset");
      fieldset.className = "amp-ai-finder__facet";
      const legend = document.createElement("legend");
      legend.textContent = facet.label;
      const choices = document.createElement("div");
      choices.className = "amp-ai-finder__facet-values";
      facet.values.slice(0, MAX_VISIBLE_VALUES).forEach((value) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "amp-ai-finder__facet-value";
        const active = activeRefinements.get(facet.paramName) === value.value;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
        button.textContent = value.label;
        button.addEventListener("click", () => {
          if (activeRefinements.get(facet.paramName) === value.value) {
            activeRefinements.delete(facet.paramName);
          } else {
            activeRefinements.set(facet.paramName, value.value);
          }
          sendEvent("refinement", { category: facet.paramName, value: value.value });
          refreshProducts();
        });
        choices.append(button);
      });
      fieldset.append(legend, choices);
      return fieldset;
    }

    function renderFacets(facets, refreshProducts) {
      const visible = facets
        .filter((facet) => facet.values.length > 1 || activeRefinements.has(facet.paramName))
        .slice(0, MAX_VISIBLE_FACETS);
      facetsNode.replaceChildren(...visible.map((facet) => createFacetGroup(facet, refreshProducts)));
      refinementsPanel.hidden = visible.length === 0;
      clearRefinements.hidden = activeRefinements.size === 0;
    }

    async function renderProducts() {
      if (!syncVehicle() || !lastAdvice) return;
      catalogueController?.abort();
      const controller = new AbortController();
      catalogueController = controller;
      const requestVehicle = vehicle;
      const requestRevision = vehicleRevision;
      const current = () => catalogueController === controller && !controller.signal.aborted &&
        requestRevision === vehicleRevision && vehicleTargetId(readVehicle()) === vehicleTargetId(requestVehicle);
      productsPanel.hidden = false;
      productGrid.replaceChildren();
      browseResults.hidden = true;
      productsStatus.textContent = "Checking the live catalogue against your vehicle...";
      productGrid.setAttribute("aria-busy", "true");
      try {
        const catalogue = await fetchVehicleProducts(
          lastAdvice?.searchTerms,
          requestVehicle,
          new Map(activeRefinements),
          controller.signal,
        );
        if (!current()) return;
        productGrid.replaceChildren(...catalogue.products.map((product) => createProductCard(product, sendEvent)));
        productGrid.removeAttribute("aria-busy");
        productsStatus.textContent = catalogue.products.length
          ? `${catalogue.products.length} product${catalogue.products.length === 1 ? "" : "s"} matched to your selected vehicle`
          : "No verified matches found for this vehicle. Try another description or contact us for help.";
        renderFacets(catalogue.facets, renderProducts);
        browseResults.href = vehicleSearchUrl(
          catalogue.primaryTerm,
          vehicle,
          "",
          activeRefinements,
        ).toString();
        // The wider catalogue also includes NONCOMPONENT items. Do not present
        // that general search as an extension of these verified vehicle matches.
        browseResults.hidden = true;
        sendEvent("result_view", { resultCount: catalogue.products.length });
      } catch (error) {
        if (!current() || error?.name === "AbortError") return;
        productGrid.removeAttribute("aria-busy");
        productsStatus.textContent = "The live catalogue could not be refreshed. Use the category links above.";
      }
    }

    async function renderResult(data) {
      lastAdvice = data;
      conversation = null;
      currentQuestion = null;
      followUp.hidden = true;
      advicePanel.hidden = true;
      results.hidden = false;
      resultsHeading.textContent = data.status === "safety" ? "Safety first" : "Likely areas to check";
      summary.textContent = clean(data.summary, 300) || "These are the most relevant areas to check first.";
      const recommendations = Array.isArray(data.recommendations)
        ? data.recommendations.filter((item) => item && item.path && item.title && item.why).slice(0, 3)
        : [];
      list.replaceChildren(...recommendations.map((item) => createResultLink(item, vehicle, sendEvent)));
      list.hidden = recommendations.length === 0;
      const checks = Array.isArray(data.checks)
        ? data.checks.filter((item) => typeof item === "string" && item.trim()).slice(0, 3)
        : [];
      checksList.replaceChildren(...checks.map(createCheck));
      checksPanel.hidden = checks.length === 0;
      safety.dataset.level = data.safety?.level || "advice";
      safety.textContent = clean(data.safety?.message, 420) ||
        "This is parts guidance, not a mechanical diagnosis. Confirm the cause and compatibility before ordering.";
      modeNote.textContent = data.status === "safety"
        ? "No products are shown for a safety-critical description."
        : data.mode === "guided"
          ? "The AI service was unavailable, so these results use protected vehicle-aware guidance."
          : "Vehicle identity and fitment use TecDoc. Live products, price and availability use Shopify.";
      feedback.hidden = false;
      feedbackStatus.textContent = "";
      if (data.status === "safety") {
        productsPanel.hidden = true;
        refinementsPanel.hidden = true;
      } else {
        activeRefinements = new Map();
        await renderProducts();
      }
      results.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    async function requestAdvice(body, button, busyText, normalText, errorNode) {
      adviceController?.abort();
      const controller = new AbortController();
      adviceController = controller;
      const requestRevision = vehicleRevision;
      const targetId = body.vehicle?.targetId;
      const current = () => adviceController === controller && requestRevision === vehicleRevision &&
        targetId === vehicleTargetId(readVehicle());
      setBusy(button, true, busyText, normalText);
      errorNode.textContent = "";
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...body, sessionId: visitorSessionId }),
          signal: controller.signal,
        });
        const data = await response.json().catch(() => null);
        if (!current()) return;
        if (!response.ok || !data) throw new Error(data?.error || "We could not check that description just now.");
        if (data.status === "clarify" && data.question && data.conversation) {
          renderQuestion(data);
        } else if (["results", "safety"].includes(data.status)) {
          await renderResult(data);
        } else {
          throw new Error("The adviser returned an incomplete answer. Please try again.");
        }
      } catch (error) {
        if (!current()) return;
        errorNode.textContent = error?.name === "AbortError"
          ? "The check took too long. Please try again."
          : error?.message || "We could not check that description just now.";
      } finally {
        window.clearTimeout(timeout);
        if (current()) {
          setBusy(button, false, busyText, normalText);
          adviceController = null;
        }
      }
    }

    syncVehicle();
    window.addEventListener("amp:vehicle-changed", () => {
      resetAdviser(false);
      syncVehicle();
    });
    window.addEventListener("storage", (event) => {
      if (event.key === "vehicle") {
        resetAdviser(false);
        syncVehicle();
      }
    });
    window.addEventListener("pageshow", syncVehicle);
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) syncVehicle();
    });

    registrationForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      const registration = String(registrationInput.value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      if (registration.length < 2) {
        registrationStatus.textContent = "Enter your vehicle registration.";
        registrationInput.focus();
        return;
      }
      if (typeof window.ampRunRegistrationLookup !== "function") {
        registrationStatus.textContent = "Vehicle lookup is still loading. Please try again.";
        return;
      }
      registrationSubmit.disabled = true;
      registrationSubmit.textContent = "Finding vehicle...";
      registrationStatus.textContent = "Running the secure registration lookup...";
      const lookup = await window.ampRunRegistrationLookup(registration, {
        challengeAnchor: registrationSubmit,
        analyticsSource: "home_ai_adviser",
        navigate: false,
      });
      registrationSubmit.disabled = false;
      registrationSubmit.textContent = "Use this vehicle";
      if (!lookup?.ok) {
        registrationStatus.textContent = lookup?.message ||
          "We could not find that vehicle. Check the registration and try again.";
        return;
      }
      vehicle = lookup.vehicle;
      registrationInput.value = "";
      syncVehicle();
      status.textContent = "Vehicle selected. Now describe the problem.";
      textarea.focus();
    });

    root.querySelectorAll("[data-ai-parts-finder-prompt]").forEach((button) => {
      button.addEventListener("click", () => {
        textarea.value = button.dataset.aiPartsFinderPrompt || button.textContent.trim();
        textarea.focus();
        status.textContent = "";
      });
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const symptom = textarea.value.trim();
      const vehicleContext = compactVehicleContext(readVehicle());
      if (!vehicleContext) {
        syncVehicle();
        registrationStatus.textContent = "Enter your registration first so results can be matched to your vehicle.";
        registrationInput.focus();
        return;
      }
      if (symptom.length < 8) {
        status.textContent = "Please add a little more detail about the problem.";
        textarea.focus();
        return;
      }
      vehicle = readVehicle();
      vehicleName.textContent = vehicleLabel(vehicle);
      initialSymptom = symptom;
      conversation = null;
      await requestAdvice(
        { symptom, vehicle: vehicleContext },
        submit,
        "Checking vehicle...",
        "Find likely parts",
        status,
      );
    });

    continueButton.addEventListener("click", async () => {
      if (!syncVehicle()) return;
      const answer = clean(answerInput.value, 180);
      if (!currentQuestion?.id || !answer) {
        followUpStatus.textContent = "Choose an answer or add a short description.";
        answerInput.focus();
        return;
      }
      const nextConversation = {
        initialSymptom: conversation?.initialSymptom || initialSymptom,
        answers: [
          ...(Array.isArray(conversation?.answers) ? conversation.answers : []),
          { id: currentQuestion.id, value: answer },
        ].slice(0, 2),
        asked: Array.isArray(conversation?.asked) ? conversation.asked : [],
      };
      conversation = nextConversation;
      await requestAdvice(
        {
          symptom: initialSymptom || nextConversation.initialSymptom,
          vehicle: compactVehicleContext(readVehicle()),
          conversation: nextConversation,
        },
        continueButton,
        "Checking...",
        "Continue",
        followUpStatus,
      );
    });

    clearRefinements.addEventListener("click", () => {
      activeRefinements = new Map();
      sendEvent("refinement", { value: "clear" });
      renderProducts();
    });

    root.querySelectorAll("[data-ai-parts-finder-start-again]").forEach((button) => {
      button.addEventListener("click", () => {
        resetAdviser(true);
        textarea.focus();
      });
    });

    root.querySelectorAll("[data-ai-parts-finder-feedback]").forEach((button) => {
      button.addEventListener("click", () => {
        const value = button.dataset.aiPartsFinderFeedback;
        sendEvent(value === "helpful" ? "feedback_helpful" : "feedback_unhelpful", { value });
        feedbackStatus.textContent = value === "helpful"
          ? "Thank you — that helps us improve."
          : "Thank you. Try adding more detail or use the refinements above.";
        feedback.querySelectorAll("button").forEach((node) => { node.disabled = true; });
      });
    });
  }

  function initialiseAll(scope) {
    scope.querySelectorAll("[data-ai-parts-finder]").forEach(initialise);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialiseAll(document));
  } else {
    initialiseAll(document);
  }
  document.addEventListener("shopify:section:load", (event) => initialiseAll(event.target));
})();
